import { BaseAccountParamsDto } from './accounts.dtos';
import { BaseListResponseDto, PhoneNumberDto } from './common.dtos';

export interface AddressDto {
  address1: string;
  address2?: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
}

export interface AccountHolderAddressDto {
  address1: string;
  address2?: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
}

export enum AccountHolderMaritalStatusDto {
  Divorced = 'divorced',
  Married = 'married',
  Single = 'single',
  Widowed = 'widowed',
}

export interface AccountHolderPhysicalAddressDto extends AccountHolderAddressDto {}

export interface AccountHolderMailingAddressDto extends AccountHolderPhysicalAddressDto {
  sameAsPhysical: boolean;
}

export enum AccountStatusDto {
  Pending = 'Pending',
  New = 'New',
  InvestigationSubmitted = 'InvestigationSubmitted',
  ReadyForBackOffice = 'ReadyForBackOffice',
  BackOffice = 'BackOffice',
  AccountSetup = 'AccountSetup',
  Complete = 'Complete',
  ActionRequired = 'ActionRequired',
  Rejected = 'Rejected',
  Suspended = 'Suspended',
  Error = 'Error',
  Canceled = 'Canceled',
  PendingSubmit = 'PendingSubmit',
}

export enum AccountHolderStatementDeliveryDto {
  Electronic = 'electronic',
  Paper = 'paper',
}
export interface AccountHolderDisclosureDto {
  userId: string;
  statementDelivery: AccountHolderStatementDeliveryDto;
  stakeholder: false | { tickerSymbol: string };
  industryEmployed: false | { firmName: string };
}

export enum AccountHolderEmploymentStatusDto {
  Employed = 'employed',
  Retired = 'retired',
  Student = 'student',
  Unemployed = 'unemployed',
}

export enum AccountHolderYearsEmployedDto {
  BetweenZeroAndOne = 'betweenZeroAndOne',
  BetweenTwoAndFive = 'betweenTwoAndFive',
  BetweenFiveAndTen = 'betweenFiveAndTen',
  BetweenTenAndTwenty = 'betweenTenAndTwenty',
  MoreThanTwenty = 'moreThanTwenty',
}

export interface AccountHolderFinancialInformationDto {
  employmentStatus: AccountHolderEmploymentStatusDto;
  employerName?: string;
  jobTitle?: string;
  yearsEmployed?: AccountHolderYearsEmployedDto;
  employerAddress?: AccountHolderAddressDto;
}

export enum AccountHolderPhoneNumberTypeDto {
  Home = 'Home',
  Work = 'Work',
  Mobile = 'Mobile',
}

export type AccountHolderPhoneNumberDto = {
  phoneNumber: string;
  type: AccountHolderPhoneNumberTypeDto;
};

export interface AccountHolderTrustedContactDto extends AccountHolderAddressDto {
  firstName: string;
  lastName: string;
  email: string;
  phones: PhoneNumberDto[];
}

export interface AccountHolderDto {
  id: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  email?: string;
  phones: Array<PhoneNumberDto>;
  ssid?: string;
  maskedSsid?: string;
  dateOfBirth?: string;
  maritalStatus?: AccountHolderMaritalStatusDto;
  numberOfDependents?: number;
  birthCountry?: string;
  citizenshipCountry?: string;
  physicalAddress?: AccountHolderPhysicalAddressDto;
  mailingAddress?: AccountHolderMailingAddressDto;
  trustedContact?: AccountHolderTrustedContactDto;
  financialInformation?: AccountHolderFinancialInformationDto;
  disclosure?: AccountHolderDisclosureDto;
  jointTenantsInCommonInterestPercent?: number;
}

export enum AccuntHolderUpdateRequestTypeDto {
  PhysicalAddress = 'PhysicalAddress',
  MailingAddress = 'MailingAddress',
  SuitabilityInformation = 'SuitabilityInformation',
  TrustedContact = 'TrustedContact',
  FinancialInformationAsset = 'FinancialInformationAsset',
  FinancialinformationEmployment = 'FinancialinformationEmployment',
  Disclosure = 'Disclosure',
  Email = 'Email',
  PhoneNumber = 'PhoneNumber',
}

export enum AccountHolderUpdateRequestStatusDto {
  PendingFirmReview = 'PendingFirmReview',
  PendingConfirm = 'PendingConfirm',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Complete = 'Complete',
}

export interface AccountHolderUpdateRequestDto {
  id: string;
  type: AccuntHolderUpdateRequestTypeDto;
  status: AccountHolderUpdateRequestStatusDto;
  original:
    | Record<string, string | number | boolean>
    | (Record<string, string | number | boolean> | string | number | boolean)[];
  updated:
    | Record<string, string | number | boolean>
    | (Record<string, string | number | boolean> | string | number | boolean)[];
}

export interface BaseAccountHolderParamsDto {
  id: string;
  accountHolderId: string;
}

export interface PatchAccountHolderBodyDto {
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  suffix?: string | null;
  email?: string;
  phones?: Array<PhoneNumberDto>;
  ssid?: string;
  dateOfBirth?: string;
  numberOfDependents?: number;
  maritalStatus?: AccountHolderMaritalStatusDto;
}

export interface PatchAccountHolderDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderBodyDto;
}

export interface CreateAccountHolderPhysicalAddressBodyDto extends AddressDto {
  citizenshipCountry: string;
  birthCountry: string;
}

export interface CreateAccountHolderPhysicalAddressDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderPhysicalAddressBodyDto; // NOTE: intentially setting patch for body because in the future 'create' will be removed
}

export interface PatchAccountHolderPhysicalAddressBodyDto extends Partial<CreateAccountHolderPhysicalAddressBodyDto> {}

export interface PatchAccountHolderPhysicalAddressDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderPhysicalAddressBodyDto;
}

export interface CreateAccountHolderMailingAddressBodyDto extends AddressDto {
  sameAsPhysical: boolean;
}

export interface CreateAccountHolderMailingAddressDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderMailingAddressBodyDto; // NOTE: intentially setting patch for body because in the future 'create' will be removed
}

export interface PatchAccountHolderMailingAddressBodyDto extends Partial<CreateAccountHolderMailingAddressBodyDto> {}

export interface PatchAccountHolderMailingAddressDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderMailingAddressBodyDto;
}

export interface CreateAccountHolderTrustedContactBodyDto extends AddressDto {
  firstName: string;
  lastName: string;
  email: string;
  phones?: Array<PhoneNumberDto>;
}

export interface CreateAccountHolderTrustedContactDto {
  params: BaseAccountParamsDto;
  body: PatchAccountHolderTrustedContactBodyDto; // NOTE: intentially setting patch for body because in the future 'create' will be removed
}

export interface PatchAccountHolderTrustedContactBodyDto extends Partial<CreateAccountHolderTrustedContactBodyDto> {}

export interface PatchAccountHolderTrustedContactDto {
  params: BaseAccountParamsDto;
  body: PatchAccountHolderTrustedContactBodyDto;
}

export interface DeleteAccountHolderTrustedContactDto {
  params: BaseAccountParamsDto;
}

export interface CreateAccountHolderFinancialInformationEmploymentBodyDto {
  employmentStatus: AccountHolderEmploymentStatusDto;
  employerName?: string;
  jobTitle?: string;
  yearsEmployed?: AccountHolderYearsEmployedDto;
  employerAddress?: AddressDto;
}

export interface CreateAccountHolderFinancialEmploymentDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderFinancialInformationEmploymentBodyDto;
}

export interface PatchAccountHolderFinancialInformationEmploymentBodyDto
  extends Partial<CreateAccountHolderFinancialInformationEmploymentBodyDto> {}

export interface PatchAccountHolderFinancialEmploymentDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderFinancialInformationEmploymentBodyDto;
}
export interface CreateAccountHolderDisclosureBodyDto {
  statementDelivery: AccountHolderStatementDeliveryDto;
  stakeholder: false | { tickerSymbol: string };
  industryEmployed: false | { firmName: string };
}

export interface CreateAccountholderDisclosureDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderDisclosureBodyDto;
}

export interface PatchAccountHolderDisclosureBodyDto extends Partial<CreateAccountHolderDisclosureBodyDto> {}

export interface PatchAccountHolderDisclosureDto {
  params: BaseAccountHolderParamsDto;
  body: PatchAccountHolderDisclosureBodyDto;
}

export interface BaseAccountHolderUpdateRequestParamsDto extends BaseAccountHolderParamsDto {
  requestId: string;
}

export interface GetAccountHolderUpdateRequestListDto {
  params: BaseAccountHolderParamsDto;
}

export interface GetAccountHolderUpdateRequestListResponseDto
  extends BaseListResponseDto<AccountHolderUpdateRequestDto[]> {}

export interface GetAccountHolderUpdateRequestDto {
  params: BaseAccountHolderUpdateRequestParamsDto;
}

export interface ApproveAccountHolderUpdateRequestBodyDto {
  note?: string;
}

export interface ApproveAccountHolderUpdateRequestDto {
  params: BaseAccountHolderUpdateRequestParamsDto;
  body: ApproveAccountHolderUpdateRequestBodyDto;
}

export interface RejectAccountHolderUpdateRequestBodyDto extends ApproveAccountHolderUpdateRequestBodyDto {}

export interface RejectAccountHolderUpdateRequestDto {
  params: BaseAccountHolderUpdateRequestParamsDto;
  body: RejectAccountHolderUpdateRequestBodyDto;
}
